import { useEffect } from 'react';

/**
 * Custom hook for iOS mobile optimizations
 * Handles status bar, keyboard, and other mobile-specific features
 */
export function useMobileOptimizations() {
  useEffect(() => {
    // Detect if running in Capacitor (native mobile app)
    const isCapacitor = window.Capacitor !== undefined;
    
    if (isCapacitor) {
      // Set status bar style for iOS
      if (window.Capacitor.Plugins.StatusBar) {
        const { StatusBar } = window.Capacitor.Plugins;
        
        // Set dark status bar for space theme
        StatusBar.setStyle({ style: 'DARK' }).catch(() => {
          console.log('StatusBar plugin not available');
        });
        
        // Set background color to black (matches space theme)
        StatusBar.setBackgroundColor({ color: '#000000' }).catch(() => {
          console.log('StatusBar setBackgroundColor not available');
        });
      }
      
      // Handle keyboard events
      if (window.Capacitor.Plugins.Keyboard) {
        const { Keyboard } = window.Capacitor.Plugins;
        
        // Configure keyboard behavior
        Keyboard.setAccessoryBarVisible({ isVisible: true }).catch(() => {
          console.log('Keyboard plugin not available');
        });
        
        // Add keyboard event listeners
        const keyboardWillShow = Keyboard.addListener('keyboardWillShow', (info) => {
          console.log('Keyboard will show:', info);
        });
        
        const keyboardWillHide = Keyboard.addListener('keyboardWillHide', () => {
          console.log('Keyboard will hide');
        });
        
        // Cleanup listeners
        return () => {
          keyboardWillShow.remove();
          keyboardWillHide.remove();
        };
      }
    }
    
    // Prevent double-tap zoom on iOS
    let lastTouchEnd = 0;
    const preventZoom = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };
    
    document.addEventListener('touchend', preventZoom, { passive: false });
    
    // Add viewport meta tag for mobile
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      );
    }
    
    return () => {
      document.removeEventListener('touchend', preventZoom);
    };
  }, []);
}

/**
 * Check if app is running on iOS
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Check if app is running in Capacitor
 */
export function isCapacitor() {
  return window.Capacitor !== undefined;
}

/**
 * Get safe area insets for iOS
 */
export function getSafeAreaInsets() {
  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0'),
    right: parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0'),
    bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0'),
    left: parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0')
  };
}

